/**
 * The answer key of a scrabble is the author's own play. Kept apart from the form component
 * so the eagerly loaded module file never pulls the author's board into the player bundle.
 */
import { onBoard, step, type Board } from "./board";
import { cellKey, type PlacedTile, type ScrabbleDefinition, type ScrabbleSolution } from "./types";

/** Nothing played yet: an empty word, which `validate_solution` refuses on save. */
export function emptySolution(_definition: ScrabbleDefinition): ScrabbleSolution {
  return { row: 7, col: 7, direction: "across", word: "" };
}

/** Whether a stored value is a play at all. */
export function isPlay(value: unknown): value is ScrabbleSolution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const play = value as Record<string, unknown>;
  return (
    Number.isInteger(play.row) &&
    Number.isInteger(play.col) &&
    (play.direction === "across" || play.direction === "down") &&
    typeof play.word === "string" &&
    /^[A-Za-z]*$/.test(play.word)
  );
}

/**
 * The tiles a play sets down on this board: one per empty cell of its span, a blank where
 * the letter is lowercase. Null when the play does not fit the board's own tiles.
 */
export function placedFromPlay(board: Board, play: ScrabbleSolution): PlacedTile[] | null {
  const [dr, dc] = step(play.direction);
  const placed: PlacedTile[] = [];
  for (let i = 0; i < play.word.length; i += 1) {
    const cell: [number, number] = [play.row + dr * i, play.col + dc * i];
    if (!onBoard(cell)) return null;
    const letter = play.word[i];
    const existing = board.get(cellKey(cell));
    if (existing) {
      if (existing.letter !== letter.toUpperCase()) return null;
      continue;
    }
    placed.push({
      r: cell[0],
      c: cell[1],
      letter: letter.toUpperCase(),
      blank: letter !== letter.toUpperCase(),
    });
  }
  return placed;
}
